'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { HelpCircle } from 'lucide-react';
import { signIn } from 'next-auth/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip.tsx';
import { Heading, Text } from 'components/ui/typography';

import { type LoginFormData, loginSchema } from './schemas';

function FormDescription() {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <HelpCircle className="size-4 text-muted-foreground" />
        </TooltipTrigger>
        <TooltipContent>
          <Text variant="description">
            You are free to choose any username and email address. <br />
            Please make sure that other user are not using the same at the same time.
          </Text>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function OfflineLoginForm() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  async function onSubmit(data: LoginFormData) {
    await signIn('credentials-offline', {
      ...data,
      callbackUrl: '/',
    });
  }

  return (
    <div className="rounded border">
      <div className="w-full max-w-sm p-6 space-y-4">
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Heading variant="cardHeading">Offline Login</Heading>
            <FormDescription />
          </div>
          <Text variant="description">
            Please enter the offline login password given to you by your Admin.
          </Text>
        </div>
        <div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input id="name" type="text" {...register('name')} />
                {errors.name && (
                  <Text variant="error" className="mt-1">
                    {errors.name.message}
                  </Text>
                )}
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" {...register('email')} />
                {errors.email && (
                  <Text variant="error" className="mt-1">
                    {errors.email.message}
                  </Text>
                )}
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" {...register('password')} />
                {errors.password && (
                  <Text variant="error" className="mt-1">
                    {errors.password.message}
                  </Text>
                )}
              </div>
            </div>
            <Button testId="login-offline-login" type="submit" className="w-full mt-4">
              Login
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

export function LoginOffline() {
  const [showForm, setShowForm] = useState(false);

  return (
    <>
      {showForm && <OfflineLoginForm />}
      <Button
        testId="login-offline"
        className="w-full"
        variant="outline"
        onClick={() => setShowForm(!showForm)}
      >
        Continue with Offline Login
      </Button>
    </>
  );
}
